import React, { useState, useEffect, useCallback } from 'react';
import { db, auth } from '../firebase';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, setDoc, serverTimestamp } from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { Container, Card, Form, Button, Spinner, Alert, Row, Col, InputGroup } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

// 都道府県リスト
const PREFECTURES = [
  "北海道", "青森県", "岩手県", "宮城県", "秋田県", "山形県", "福島県",
  "茨城県", "栃木県", "群馬県", "埼玉県", "千葉県", "東京都", "神奈川県",
  "新潟県", "富山県", "石川県", "福井県", "山梨県", "長野県", "岐阜県",
  "静岡県", "愛知県", "三重県", "滋賀県", "京都府", "大阪府", "兵庫県",
  "奈良県", "和歌山県", "鳥取県", "島根県", "岡山県", "広島県", "山口県",
  "徳島県", "香川県", "愛媛県", "高知県", "福岡県", "佐賀県", "長崎県",
  "熊本県", "大分県", "宮崎県", "鹿児島県", "沖縄県"
];

// 全角数字を半角数字に変換するヘルパー関数
const toHalfWidth = (str) => {
  if (!str) return '';
  return str.replace(/[０-９]/g, (s) => {
    return String.fromCharCode(s.charCodeAt(0) - 0xFEE0);
  });
};

// 画面幅を検知するためのカスタムフック
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const listener = () => setMatches(media.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [query]);
  return matches;
};

// 電話番号を適切な区切りで分割するヘルパー関数
const getPhoneParts = (phone) => {
  if (!phone) return { part1: '', part2: '', part3: '' };
  const phoneStr = String(phone);
  if (phoneStr.includes('-')) {
    const parts = phoneStr.split('-');
    return { part1: parts[0] || '', part2: parts[1] || '', part3: parts[2] || '' };
  }
  const cleanPhone = phoneStr.replace(/[^0-9]/g, '');
  const len = cleanPhone.length;
  if (len === 10) {
    if (cleanPhone.startsWith('03') || cleanPhone.startsWith('06')) {
      return { part1: cleanPhone.slice(0, 2), part2: cleanPhone.slice(2, 6), part3: cleanPhone.slice(6) };
    } else {
      return { part1: cleanPhone.slice(0, 3), part2: cleanPhone.slice(3, 6), part3: cleanPhone.slice(6) };
    }
  } else if (len === 11) {
    return { part1: cleanPhone.slice(0, 3), part2: cleanPhone.slice(3, 7), part3: cleanPhone.slice(7) };
  } else {
    if (len <= 3) return { part1: cleanPhone, part2: '', part3: '' };
    else if (len <= 7) return { part1: cleanPhone.slice(0, 3), part2: cleanPhone.slice(3), part3: '' };
    else return { part1: cleanPhone.slice(0, 3), part2: cleanPhone.slice(3, 7), part3: cleanPhone.slice(7) };
  }
};

function ProfileSettings() {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const currentUser = auth.currentUser;

  const [phoneParts, setPhoneParts] = useState({ part1: '', part2: '', part3: '' });
  const [prefectureWarning, setPrefectureWarning] = useState('');
  const [zipCodeWarning, setZipCodeWarning] = useState('');

  useEffect(() => {
    document.title = 'プロファイル設定 | My Order System';
    if (!currentUser) { navigate('/login'); return; }
    const fetchData = async () => {
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          let companyData = {};
          if (userData.companyId) {
            const companyDocRef = doc(db, 'companies', userData.companyId);
            const companyDocSnap = await getDoc(companyDocRef);
            if (companyDocSnap.exists()) { companyData = companyDocSnap.data(); }
          }
          
          // ★ ユーザー側の情報を優先し、なければ会社側から読み込む
          const mergedData = { 
            ...userData, 
            ...companyData,
            branchName: userData.branchName || companyData.branchName || '',
            departmentName: userData.departmentName || companyData.departmentName || ''
          };
          setProfileData(mergedData);

          if (mergedData.phone) {
            setPhoneParts(getPhoneParts(mergedData.phone));
          }
        } else { setError('ユーザーデータが見つかりません。'); }
      } catch (err) { setError('データの取得に失敗しました。'); console.error(err); }
      setLoading(false);
    };
    fetchData();
  }, [currentUser, navigate]);

  useEffect(() => { if (profileData?.companyPrefix) { setProfileData(prev => ({ ...prev, companySuffix: '' })); } }, [profileData?.companyPrefix]);
  useEffect(() => { if (profileData?.companySuffix) { setProfileData(prev => ({ ...prev, companyPrefix: '' })); } }, [profileData?.companySuffix]);
  useEffect(() => { if (profileData?.isSoleProprietor) { setProfileData(prev => ({ ...prev, companyPrefix: '', companySuffix: '' })); } }, [profileData?.isSoleProprietor]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = type === 'checkbox' ? checked : value;

    if (name === 'zipCode') {
      const halfWidthValue = toHalfWidth(value);
      const numericValue = halfWidthValue.replace(/[^0-9]/g, '');
      processedValue = numericValue.slice(0, 7);
      if (halfWidthValue !== numericValue) setZipCodeWarning('半角数字のみ入力可能です。');
      else if (processedValue.length > 0 && processedValue.length !== 7) setZipCodeWarning('郵便番号は7桁で入力してください。');
      else setZipCodeWarning('');
    }

    setProfileData(prev => ({ ...prev, [name]: processedValue }));

    if (name === 'address1') {
      if (processedValue && !PREFECTURES.includes(processedValue)) setPrefectureWarning('正しい都道府県名を入力してください。');
      else setPrefectureWarning('');
    }
  };

  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = value.replace(/[^0-9]/g, '');
    const newPhoneParts = { ...phoneParts, [name]: sanitizedValue };
    setPhoneParts(newPhoneParts);
    const combinedPhone = [newPhoneParts.part1, newPhoneParts.part2, newPhoneParts.part3].filter(Boolean).join('-');
    setProfileData(prev => ({ ...prev, phone: combinedPhone }));
  };

  const handleUpdate = async () => {
    if (!profileData) return;

    // バリデーションチェック
    if (profileData.address1 && !PREFECTURES.includes(profileData.address1)) {
        setError('都道府県が正しくありません。修正してください。');
        setPrefectureWarning('正しい都道府県名を入力してください。');
        return;
    }
    if (profileData.zipCode && profileData.zipCode.length !== 7) {
        setError('郵便番号は7桁で入力してください。');
        setZipCodeWarning('郵便番号は7桁で入力してください。');
        return;
    }

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      let companyIdToSet = null;

      const companiesRef = collection(db, "companies");

      // ★ 修正：会社名、法人格、支店名、部署名がすべて一致するドキュメントを検索
      const q = query(
        companiesRef,
        where("companyName", "==", profileData.companyName || ""),
        where("companyPrefix", "==", profileData.companyPrefix || ""),
        where("companySuffix", "==", profileData.companySuffix || ""),
        where("branchName", "==", profileData.branchName || ""),
        where("departmentName", "==", profileData.departmentName || ""),
        where("isSoleProprietor", "==", !!profileData.isSoleProprietor)
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // 一致する会社情報が見つかった場合、そのドキュメントのIDを使用
        companyIdToSet = querySnapshot.docs[0].id;
      } else {
        // 一致する情報がない場合、新しい会社ドキュメントを作成
        companyIdToSet = uuidv4();
        const newCompanyData = {
          companyId: companyIdToSet,
          companyName: profileData.companyName || "",
          companyPrefix: profileData.companyPrefix || "",
          companySuffix: profileData.companySuffix || "",
          branchName: profileData.branchName || "",
          departmentName: profileData.departmentName || "",
          isSoleProprietor: !!profileData.isSoleProprietor,
          createdAt: serverTimestamp(),
        };
        await setDoc(doc(db, 'companies', companyIdToSet), newCompanyData);
      }

      // ★ ユーザー情報の更新（新しい companyId をセット）
      const userDataToUpdate = {
        personName: profileData.personName || "",
        phone: profileData.phone || "",
        zipCode: profileData.zipCode || "",
        address1: profileData.address1 || "",
        address2: profileData.address2 || "",
        address3: profileData.address3 || "",
        // branchName: profileData.branchName || "",
        // departmentName: profileData.departmentName || "",
        companyId: companyIdToSet,
      };
      await updateDoc(userDocRef, userDataToUpdate);

      // stateを更新して画面表示に反映
      setProfileData(prev => ({ ...prev, companyId: companyIdToSet }));
      setSuccess('プロファイル情報を更新しました。');
    } catch (err) {
      setError('情報の更新に失敗しました。');
      console.error(err);
    }
    setSaving(false);
  };

  const handlePasswordReset = () => {
    if (window.confirm('パスワード再設定メールを送信します。よろしいですか？')) {
      sendPasswordResetEmail(auth, currentUser.email)
        .then(() => { setSuccess('パスワード再設定メールを送信しました。'); })
        .catch((err) => { setError('メールの送信に失敗しました。'); console.error(err); });
    }
  };

  const handlePostalCodeSearch = useCallback(async () => {
    if (profileData && profileData.zipCode && profileData.zipCode.length === 7) {
      try {
        const res = await axios.get('https://zipcloud.ibsnet.co.jp/api/search', { params: { zipcode: profileData.zipCode } });
        if (res.data.results) {
          const result = res.data.results[0];
          setProfileData(prev => ({ ...prev, address1: result.address1, address2: result.address2 + result.address3 }));
          setPrefectureWarning('');
          setZipCodeWarning('');
        } else { alert('該当する住所が見つかりませんでした。'); }
      } catch (err) { alert('住所の自動入力に失敗しました。'); }
    } else { 
        alert('7桁の郵便番号を入力してください。');
        if(profileData.zipCode.length !== 7) setZipCodeWarning('郵便番号は7桁で入力してください。');
    }
  }, [profileData]);

  if (loading) return <Container className="d-flex justify-content-center align-items-center vh-100"><Spinner animation="border" /></Container>;
  if (!profileData) return <Container className="my-4"><Alert variant="danger">データの読み込みに失敗しました。</Alert></Container>;

  return (
    <main>
      <Container className="my-4">
        <Card className="shadow-sm">
          <Card.Header className="p-3 d-flex flex-column flex-md-row justify-content-md-between align-items-md-end">
            <h2 className="mb-2 mb-md-0">ユーザー情報変更</h2>
            <Link to="/mainmenu"><small>メインメニューに戻る</small></Link>
          </Card.Header>
          <Card.Body className="p-4">
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}
            <Form>
              <h5>ユーザー情報</h5>
              <Row>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>氏名</Form.Label><Form.Control type="text" name="personName" value={profileData.personName} onChange={handleChange} /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>メールアドレス</Form.Label><Form.Control type="email" value={currentUser.email} readOnly disabled /></Form.Group></Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>電話番号</Form.Label>
                    <Row className="g-2">
                      <Col><Form.Control type="text" name="part1" value={phoneParts.part1} onChange={handlePhoneChange} maxLength="4" /></Col>
                      <Col xs="auto" className="d-flex align-items-center">-</Col>
                      <Col><Form.Control type="text" name="part2" value={phoneParts.part2} onChange={handlePhoneChange} maxLength="4" /></Col>
                      <Col xs="auto" className="d-flex align-items-center">-</Col>
                      <Col><Form.Control type="text" name="part3" value={phoneParts.part3} onChange={handlePhoneChange} maxLength="4" /></Col>
                    </Row>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>郵便番号</Form.Label>
                    <InputGroup>
                      <Form.Control type="text" name="zipCode" value={profileData.zipCode} onChange={handleChange} maxLength="7" placeholder="例: 1234567" isInvalid={!!zipCodeWarning} />
                      <Button variant="secondary" onClick={handlePostalCodeSearch}>住所検索</Button>
                      <Form.Control.Feedback type="invalid" style={{display: 'block'}}>{zipCodeWarning}</Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>住所1(都道府県)</Form.Label>
                    <Form.Control type="text" name="address1" value={profileData.address1} onChange={handleChange} isInvalid={!!prefectureWarning} />
                    <Form.Control.Feedback type="invalid">{prefectureWarning}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={12}><Form.Group className="mb-3"><Form.Label>住所2(市区町村以降)</Form.Label><Form.Control type="text" name="address2" value={profileData.address2} onChange={handleChange} /></Form.Group></Col>
                <Col md={12}><Form.Group className="mb-3"><Form.Label>住所3(ビル名、部屋番号)</Form.Label><Form.Control type="text" name="address3" value={profileData.address3} onChange={handleChange} /></Form.Group></Col>
              </Row>
              <hr />
              <h5>会社情報</h5>
              <Row>
                <Col md={12}><Form.Group className="mb-3"><Form.Check type="checkbox" name="isSoleProprietor" label="個人事業主" checked={profileData.isSoleProprietor || false} onChange={handleChange} /></Form.Group></Col>
                <Col md={12}><Form.Group className="mb-3"><Form.Label>会社名</Form.Label><Form.Control type="text" name="companyName" value={profileData.companyName} onChange={handleChange} /></Form.Group></Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>法人格 (前)</Form.Label>
                    <Form.Select name="companyPrefix" value={profileData.companyPrefix} onChange={handleChange} disabled={profileData.isSoleProprietor}><option value="">なし</option><option value="株式会社">株式会社</option><option value="有限会社">有限会社</option></Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>法人格 (後)</Form.Label>
                    <Form.Select name="companySuffix" value={profileData.companySuffix} onChange={handleChange} disabled={profileData.isSoleProprietor}><option value="">なし</option><option value="株式会社">株式会社</option><option value="有限会社">有限会社</option></Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>支店名</Form.Label><Form.Control type="text" name="branchName" value={profileData.branchName} onChange={handleChange} /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>部署名</Form.Label><Form.Control type="text" name="departmentName" value={profileData.departmentName} onChange={handleChange} /></Form.Group></Col>
              </Row>
              <div className="d-grid mt-3">
                <Button variant="primary" onClick={handleUpdate} disabled={saving}>{saving ? <Spinner as="span" size="sm" /> : 'プロファイルを更新'}</Button>
              </div>
              <div className="d-grid mt-2"><Button variant="outline-danger" onClick={handlePasswordReset}>パスワードを再設定する</Button></div>
            </Form>
            <hr className="my-4" />
            <Link to="/mainmenu">メインメニューに戻る</Link>
          </Card.Body>
        </Card>
      </Container>
    </main>
  );
}

export default ProfileSettings;